import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { api } from "@/api";
import { C } from "@/theme";
import { Button } from "@/ui";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    setError("");
    if (pw.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (pw !== confirm) { setError("Passwords do not match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
    } catch (e) {
      setError(e?.message || "This reset link is invalid or has expired.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: C.surface }}>
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center gap-3 mb-6">
          <img src="/rospa-logo.png" alt="RoSPA Advanced Riders West Midlands" className="w-56 object-contain" />
          <p className="text-white text-xl font-extrabold font-display tracking-tight">WMR Training App</p>
        </div>
        <div style={{ background: C.surfaceSecondary, borderColor: C.border }} className="rounded-3xl border p-6">
          {done ? (
            <div className="text-center flex flex-col items-center gap-3" data-testid="reset-success">
              <CheckCircle2 size={44} color={C.success} />
              <p style={{ color: C.onSurface }} className="font-extrabold text-lg">Password updated</p>
              <p style={{ color: C.muted }} className="text-[13px]">You can now sign in with your new password.</p>
              <div className="w-full mt-2"><Button label="Go to sign in" testID="reset-goto-login" onClick={() => navigate("/login")} /></div>
            </div>
          ) : !token ? (
            <p data-testid="reset-no-token" style={{ color: C.error }} className="text-[13px] font-semibold text-center">This reset link is missing its token. Please request a new one from the sign-in page.</p>
          ) : (
            <>
              <label style={{ color: C.onSurfaceTertiary }} className="text-[13px] font-bold">New password</label>
              <div style={{ background: C.surfaceTertiary, borderColor: C.border }} className="flex items-center rounded-xl border px-3.5 mt-2">
                <input data-testid="reset-password" type={showPw ? "text" : "password"} value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 6 characters" style={{ color: C.onSurface }} className="flex-1 bg-transparent h-12 text-[15px] outline-none" />
                <button data-testid="reset-toggle" onClick={() => setShowPw((v) => !v)}>{showPw ? <EyeOff size={20} color={C.muted} /> : <Eye size={20} color={C.muted} />}</button>
              </div>
              <label style={{ color: C.onSurfaceTertiary }} className="text-[13px] font-bold mt-4 block">Confirm new password</label>
              <div style={{ background: C.surfaceTertiary, borderColor: C.border }} className="flex items-center rounded-xl border px-3.5 mt-2">
                <input data-testid="reset-confirm" type={showPw ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} placeholder="Re-enter password" style={{ color: C.onSurface }} className="flex-1 bg-transparent h-12 text-[15px] outline-none" />
              </div>
              {error ? <p data-testid="reset-error" style={{ color: C.error }} className="text-[13px] font-semibold mt-3">{error}</p> : null}
              <div className="mt-5"><Button label="Set new password" testID="reset-submit" onClick={submit} disabled={!pw || !confirm} loading={busy} /></div>
              <button data-testid="reset-back" onClick={() => navigate("/login")} style={{ color: C.mutedInverse }} className="text-[12px] font-semibold mt-3 underline w-full text-center">Back to sign in</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
